import { FETCH_LIVE_ACCEL_POLL } from '@/actions/types';
import { AccelerometerChart } from '@/components/events';
import {
  useClaimAccelerometerAlert,
  useDocumentTitle,
  useOptionValues,
} from '@/hooks';
import { round } from '@/utils';
import { dioStates, eventNotifications } from '@/utils/config';
import {
  LocationSearching as FollowIcon,
  HowToReg as HowToRegIcon,
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { groupsToLabelAccessors, toLabelAccessors } from '../utils';
import { ItemAvatar } from './ItemAvatar';
import { ItemRows } from './ItemRows';
import { PeopleLiveListItem } from './PeopleLiveListItem';
import { VehicleLiveListItem } from './VehicleLiveListItem';

// example object
// $type: "add"
// eventType: "ACCELEROMETERALERT"
// identifier: "ACCELEROMETERALERT-2018-05-17T13:50:35.000Z-HA0812"
// locations: Array(1)
// 0: {code: "OS_WMIDQNT", name: "West Mids RCC & Quinton Outstation", type: "Base"}
// people: Array(0)
// length: 0
// point:
// $reql_type$: "GEOMETRY"
// coordinates: (2) [-2.009744, 52.453684]
// type: "Point"
// searchString: "accelerometeralert+accelerometeralert-2018-05-17t13:50:35.000z-ha0812+os_wmidqnt+west mids rcc & quinton outstation+base++geometry++point+2018-05-17t13:50:35.000z+west midlands+region+ha0812+west mids rcc & quinton outstation+ha0812+bw17 ztf+traffic officer+359394057295853+add"
// tagChanged: true
// time: "2018-05-17T13:50:35.000Z"
// vehicle:
// areas: Array(1)
// 0: {name: "West Midlands", type: "region"}
// fleetNumber: "HA0812"
// homeStation: "West Mids RCC & Quinton Outstation"
// identificationNumber: "HA0812"
// registrationNumber: "BW17 ZTF"
// role: "Traffic Officer"
// telematicsBoxImei: "359394057295853"

export function EventLiveItem({
  item,
  onSubItemClick,
  onSubItemHover,
  onFollowToggle,
  followedIdsByType,
  hoveredId,
}) {
  useDocumentTitle(
    ['IR3', 'Live', 'Events', item?.identifier].filter(Boolean).join(' | '),
  );
  const type = 'events';
  const following = followedIdsByType?.[type]?.[item.id];
  const hideFollow = !item.point;
  const { mutate: claimAccelerometerAlert } = useClaimAccelerometerAlert();
  const vehicleGroups = useOptionValues('vehicleGroup');

  const handleFollowToggle = () => {
    onFollowToggle(type, item.id);
  };

  // find associated vehicle
  const vehicles = useSelector((state) => state.live.vehicles);
  const vehicle = vehicles[item.vehicle.identificationNumber];

  const people = useSelector((state) => state.live.people);
  const matchedDriver = item.driver && people[item.driver.code];

  const fullAccelEvents = useSelector(
    (state) => state.live.accelerometerEvents,
  );
  const [matchedAccelData, setMatchedAccelData] = useState();
  useEffect(() => {
    if (!matchedAccelData && fullAccelEvents) {
      const data = Object.values(fullAccelEvents).find(
        (d) =>
          d.vehicle.identificationNumber ===
            item.vehicle.identificationNumber &&
          d.startTime <= item.time &&
          item.time <= d.endTime,
      );

      if (data) {
        // change to mph
        data.accelerometerData.forEach(
          (a) =>
            a.time.includes('.000') &&
            (a.speedMilesPerHour = round(
              a.speedKilometresPerHour * 0.62137119,
              2,
            )),
        );
        setMatchedAccelData(data);
      }
    }
  }, [fullAccelEvents, item, matchedAccelData]);

  function getDriverSection(driverName, driver) {
    driver = matchedDriver || driver;
    if (!driver) {
      return <Fragment />;
    }

    let title = driverName;
    if (title === null || title.match(/^ *$/) !== null) {
      title = driver.code;
    }

    function handleClick() {
      if (matchedDriver) {
        onSubItemClick({ id: matchedDriver.id, type: 'people' });
      } // o/w nothing to nav to
    }

    return (
      <PeopleLiveListItem
        onClick={matchedDriver ? handleClick : undefined}
        onHoverItem={matchedDriver ? onSubItemHover : undefined}
        item={matchedDriver || { id: '_nomatch', name: driverName, role: '' }}
        highlighted={hoveredId === driver.code}
        onFollowToggle={onFollowToggle}
        followedIdsByType={followedIdsByType}
        hideFollow={!matchedDriver}
      />
    );
  }

  const dispatch = useDispatch();
  useEffect(() => {
    if (item.poll === undefined) {
      dispatch({
        type: FETCH_LIVE_ACCEL_POLL,
        payload: item,
      });
    }
  }, [item, dispatch]);

  const sections = {
    details: [
      {
        label: 'Event time',
        value: format(new Date(item.time), 'dd/MM/yyyy HH:mm:ss'),
      },
      { label: 'Type', value: item.eventType },
      ...toLabelAccessors(item.locations, 'type', 'name'),
      item.poll?.speedKilometresPerHour && {
        label: 'Speed',
        value: `${round(item.poll.speedKilometresPerHour * 0.62137199, 2)} MPH`,
      },
      ...Object.keys(dioStates).map(
        (key) =>
          item.poll?.[key] !== undefined && {
            label: dioStates[key],
            value: item.poll[key] ? 'On' : 'Off',
          },
      ),
      item.poll?.error && {
        label: 'Error',
        value: 'unable to retrive poll details',
      },
      !matchedAccelData && { label: 'Has Event Data', value: 'No' },
    ].filter(Boolean),
    vehicle: [
      { label: 'Fleet Number', value: item.vehicle.fleetNumber },
      { label: 'Registration', value: item.vehicle.registrationNumber },
      { label: 'Role', value: item.vehicle.role },
      { label: 'Home Station', value: item.vehicle.homeStation },
      {
        label: 'Identification Number',
        value: item.vehicle.identificationNumber,
      },
      { label: 'IMEI', value: item.vehicle.telematicsBoxImei },
      ...groupsToLabelAccessors(vehicleGroups, item.vehicle.groups),
    ],
    data: [
      {
        label: 'Max Horizontal Force (g)',
        value: matchedAccelData?.maximumForces?.horizontal,
      },
      {
        label: 'Max Vertical Force (g)',
        value: matchedAccelData?.maximumForces?.vertical,
      },
      {
        label: 'Max Lateral Force (g)',
        value: matchedAccelData?.maximumForces?.lateral,
      },
    ],
  };

  function handleClaimClick() {
    if (!item.claimedBy) {
      claimAccelerometerAlert({ identifier: item.identifier });
    }
  }

  return (
    <Card sx={{ m: 1 }}>
      <CardHeader
        avatar={<ItemAvatar item={item} type="events" />}
        title={item.vehicle.registrationNumber}
        subheader={format(new Date(item.time), 'dd/MM/yyyy HH:mm')}
        action={
          <Fragment>
            {eventNotifications.accelerometerAlerts && (
              <Tooltip
                title={
                  item.claimedBy ? `Claimed by ${item.claimedBy}` : 'Claim'
                }
              >
                <IconButton
                  onClick={handleClaimClick}
                  size="large"
                  color={item.claimedBy ? 'primary' : 'default'}
                >
                  <HowToRegIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!hideFollow && (
              <Tooltip title="Toggle follow event">
                <IconButton onClick={handleFollowToggle} size="large">
                  <FollowIcon
                    fontSize="small"
                    color={following ? 'primary' : 'disabled'}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Fragment>
        }
      />
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Alert Details
        </Typography>
        <Table size="small" sx={{ mt: 1, mb: 2 }}>
          <TableBody>{ItemRows(sections.details)}</TableBody>
        </Table>
        {matchedAccelData?.accelerometerData && (
          <Fragment>
            <Typography variant="subtitle2" color="textSecondary">
              Event Data
            </Typography>
            <Table size="small" sx={{ mt: 1, mb: 2 }}>
              <TableBody>{ItemRows(sections.data)}</TableBody>
            </Table>
            <AccelerometerChart
              data={matchedAccelData.accelerometerData}
              triggerPoint={matchedAccelData.deviceProperties.triggerPoint}
              dateFormat={'HH:mm:ss'}
              margin={{ top: 5, right: -5, bottom: 0, left: -5 }}
            />
          </Fragment>
        )}
        <Typography variant="subtitle2" color="textSecondary">
          Driver
        </Typography>
        {getDriverSection(item.driverName, item.driver)}
        <Typography variant="subtitle2" color="textSecondary">
          Vehicle
        </Typography>
        {vehicle ? (
          <VehicleLiveListItem
            key={vehicle.id}
            onClick={onSubItemClick}
            highlighted={hoveredId === vehicle.id}
            onHoverItem={onSubItemHover}
            onFollowToggle={onFollowToggle}
            followedIdsByType={followedIdsByType}
            item={vehicle}
            hideLive={false}
          />
        ) : (
          <Table size="small" sx={{ mt: 1, mb: 2 }}>
            <TableBody>{ItemRows(sections.vehicle)}</TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
